<template>
  <div class="questionnaire-container">
    <Question
      v-for="(question) in questionList"
      v-model="question.answer"
      :ref="`q_${question.id}`"
      :key="question.id"
      :type="question.type"
      :title="question.title"
      :surveyType="question.surveyType"
      :isRequired="question.isRequired"
      :optionList="question.optionList"
      :uploaderNeedFileDescription="question.uploaderNeedFileDescription"
      :uploaderFileDescription.sync="question.uploaderFileDescription"
      :uploaderFileTypeList="question.uploaderFileTypeList"
      :uploaderFileSize="question.uploaderFileSize"
      :uploaderTipList="question.uploaderTipList"
      :policyLink="question.policyLink"
      :errorMessage="question.errorMessage"
      @change="handleQuestionAnswerChange(question, $event)"
      @file-upload="handleFileUpload(question, ...arguments)"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import Question, { QUESTION_TYPE } from '@/components/candidate/editor/question.vue'
import { QUESTION_TYPE as OPEN_QUESTION_TYPE } from '@/constant/openQuestion'
import { ONBOARDING_QUESTION_TYPE } from '@/constant/common'
import { addI18nProperty } from '@/i18n'
import { uploadFile, getFileUID } from '@/utils/qiniu'
import { mapActions } from 'vuex'

export const QUESTIONNAIRE_TYPE = {
  OPEN_QUESTION: 'OPEN_QUESTION',
  ONBOARDING: 'ONBOARDING'
}

const OPEN_QUESTION_TYPE_MAP = {
  [OPEN_QUESTION_TYPE.VIDEO]: QUESTION_TYPE.VIDEO,
  [OPEN_QUESTION_TYPE.ATTACHMENT]: QUESTION_TYPE.ATTACHMENT,
  [OPEN_QUESTION_TYPE.SELECT]: QUESTION_TYPE.RADIO,
  [OPEN_QUESTION_TYPE.CHECKBOX]: QUESTION_TYPE.CHECKBOX,
  [OPEN_QUESTION_TYPE.TEXT]: QUESTION_TYPE.TEXTAREA,
  [OPEN_QUESTION_TYPE.SINGLE_SELECT]: QUESTION_TYPE.SELECT,
  [OPEN_QUESTION_TYPE.MULTIPLE_SELECT]: QUESTION_TYPE.MULTIPLE

}

const ONBOARDING_QUESTION_TYPE_MAP = {
  [QUESTION_TYPE.TEXT]: [ONBOARDING_QUESTION_TYPE.TEXT.value],
  [QUESTION_TYPE.SELECT]: [
    ONBOARDING_QUESTION_TYPE.SINGLE.value,
    ONBOARDING_QUESTION_TYPE.LOCATION.value,
    ONBOARDING_QUESTION_TYPE.NATIONALITY.value,
    ONBOARDING_QUESTION_TYPE.COUNTRY.value,
    ONBOARDING_QUESTION_TYPE.PROVINCE.value,
    ONBOARDING_QUESTION_TYPE.CITY.value,
    ONBOARDING_QUESTION_TYPE.LANGUAGE.value
  ],
  [QUESTION_TYPE.DATE]: [ONBOARDING_QUESTION_TYPE.DATE.value],
  [QUESTION_TYPE.ATTACHMENT]: [ONBOARDING_QUESTION_TYPE.ATTACHMENT.value],
  [QUESTION_TYPE.POLICY]: [ONBOARDING_QUESTION_TYPE.LINK.value]
}

export default {
  components: {
    Question
  },
  props: {
    questionnaireType: {
      type: String,
      default: QUESTIONNAIRE_TYPE.OPEN_QUESTION
    },
    questionnaire: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      questionList: []
    }
  },
  computed: {
    questionListWatcher () {
      return this.questionnaire && this.questionnaireType
    }
  },
  created () {

  },
  methods: {
    ...mapActions('application', ['handleOpenQuestionUploadedVideo', 'getOpenQuestionUploadedVideoDuration']),
    formatOpenQuestion (questionList) {
      const vm = this
      return _.map(questionList, question => {
        const openQuestionType = question.type
        const questionType = OPEN_QUESTION_TYPE_MAP[openQuestionType]
        let answer = question.answer_value
        let uploaderFileTypeList = null
        const uploaderFileSize = 200 * 1024 * 1024
        let uploaderFileDescription = null
        if (openQuestionType === OPEN_QUESTION_TYPE.SELECT || openQuestionType === OPEN_QUESTION_TYPE.SINGLE_SELECT) {
          answer = answer && parseInt(answer)
        } else if (openQuestionType === OPEN_QUESTION_TYPE.CHECKBOX || openQuestionType === OPEN_QUESTION_TYPE.MULTIPLE_SELECT) {
          answer = _.chain(answer).split(',').map(item => parseInt(item)).filter().value()
        } else if (openQuestionType === OPEN_QUESTION_TYPE.VIDEO) {
          uploaderFileTypeList = ['video/mp4', 'video/x-msvideo', 'video/ogg', 'video/quicktime']
          uploaderFileDescription = question.answer_value
          answer = question.answer_file_key
        } else if (openQuestionType === OPEN_QUESTION_TYPE.ATTACHMENT) {
          uploaderFileDescription = question.answer_value
          answer = question.answer_file_key
        }
        const questionInfo = {
          _question: question,
          id: question.id,
          type: questionType,
          title: question.title,
          isRequired: false,
          answer,
          surveyType: question.survey_type,
          optionList: _.map(question.options, option => {
            return {
              label: option.description,
              value: option.id
            }
          }),
          uploaderFileTypeList,
          uploaderFileSize,
          uploaderNeedFileDescription: [OPEN_QUESTION_TYPE.VIDEO, OPEN_QUESTION_TYPE.ATTACHMENT].includes(openQuestionType),
          uploaderFileDescription,
          errorMessage: null
        }
        if (openQuestionType === OPEN_QUESTION_TYPE.VIDEO) {
          Object.defineProperty(questionInfo, 'uploaderTipList', {
            configurable: true,
            enumerable: true,
            get () {
              return [
                vm.$t(`${vm.i18nPrefix}.message.uploadTip.openQuestionVideo`, { duration: question.duration })
              ]
            }
          })
        } else if (openQuestionType === OPEN_QUESTION_TYPE.ATTACHMENT) {
          Object.defineProperty(questionInfo, 'uploaderTipList', {
            configurable: true,
            enumerable: true,
            get () {
              return [
                vm.$t(`${vm.i18nPrefix}.message.uploadTip.attachment`, { size: 200 })
              ]
            }
          })
        }
        return questionInfo
      })
    },
    formatOnboardingQuestion (questionList) {
      const vm = this
      const questionPidMap = {}
      const questionMap = {}
      const questionInfoList = _.map(questionList, question => {
        const onboardingQuestionType = question.type
        const questionType = _.findKey(ONBOARDING_QUESTION_TYPE_MAP, typeList => typeList.includes(onboardingQuestionType))
        const uploaderFileSize = 200 * 1024 * 1024
        const questionInfo = {
          _question: question,
          _parent: null,
          _parentAnswer: null,
          id: question.id,
          type: questionType,
          isRequired: question.is_required,
          answer: question.answer,
          optionList: _.map(question.options, option => addI18nProperty({
            id: option.id,
            pid: option.pid,
            value: option.id
          }, 'label', 'content.cn', 'content.en', option)),
          uploaderFileSize,
          errorMessage: null
        }
        addI18nProperty(questionInfo, 'title', 'title.cn', 'title.en', question)
        if (onboardingQuestionType === ONBOARDING_QUESTION_TYPE.LINK.value) {
          questionInfo.answer = question.answer === '1'
          addI18nProperty(questionInfo, 'policyLink', 'options[0].content.cn', 'options[0].content.en', question)
        } else if (onboardingQuestionType === ONBOARDING_QUESTION_TYPE.ATTACHMENT.value) {
          Object.defineProperty(questionInfo, 'uploaderTipList', {
            configurable: true,
            enumerable: true,
            get () {
              return [
                vm.$t(`${vm.i18nPrefix}.message.uploadTip.attachment`, { size: 200 })
              ]
            }
          })
        }
        if (questionInfo.type === QUESTION_TYPE.SELECT) {
          questionInfo.answer = parseInt(questionInfo.answer) || null
        }
        questionMap[questionInfo.id] = questionInfo
        const pid = question.pid
        if (pid) {
          const parent = questionMap[pid]
          if (parent) {
            questionInfo._parent = parent
            questionInfo._parentAnswer = parent.answer
          } else {
            let parentChildren = questionPidMap[pid]
            if (!parentChildren) {
              questionPidMap[pid] = parentChildren = []
            }
            parentChildren.push(questionInfo)
          }
          questionInfo._optionList = questionInfo.optionList
          questionInfo._answer = questionInfo.answer
          Object.defineProperty(questionInfo, 'optionList', {
            configurable: true,
            enumerable: true,
            get () {
              const parent = this._parent
              if (!parent.answer) {
                return []
              }
              const parentOption = _.find(parent.optionList, { id: parent.answer })
              return _.filter(this._optionList, { pid: parentOption.id })
            }
          })
          Object.defineProperty(questionInfo, 'answer', {
            configurable: true,
            enumerable: true,
            get () {
              const parent = this._parent
              if (!parent.answer || parent.answer !== this._parentAnswer) {
                return null
              }
              return this._answer
            },
            set (val) {
              const parent = this._parent
              this._parentAnswer = parent.answer
              this._answer = val
            }
          })
        }
        const children = questionPidMap[questionInfo.id]
        if (children) {
          _.forEach(children, child => {
            child._parent = questionInfo
            child._parentAnswer = questionInfo.answer
          })
        }
        return questionInfo
      })
      return questionInfoList
    },
    handleQuestionAnswerChange (questionInfo, answer) {
      if (!answer && questionInfo.isRequired) {
        questionInfo.errorMessage = this.$t(`${this.i18nPrefix}.message.requiredQuestionAnswer`)
      } else {
        questionInfo.errorMessage = null
      }
    },
    async handleFileUpload (questionInfo, file, callback) {
      let errorMessage = null
      if (questionInfo.uploaderFileTypeList && !questionInfo.uploaderFileTypeList.includes(file.type)) {
        errorMessage = this.$t(`${this.i18nPrefix}.message.wrongFileType`)
      } else if (questionInfo.uploaderFileSize && file.size > questionInfo.uploaderFileSize) {
        errorMessage = this.$t(`${this.i18nPrefix}.message.wrongFileSize`)
      }
      if (errorMessage) {
        questionInfo.errorMessage = errorMessage
        callback()
        return
      }
      questionInfo.isUploading = true
      const fileName = file.name
      const ext = fileName.slice(fileName.indexOf('.'))
      const fileKey = `${getFileUID()}${ext}`
      await uploadFile(file, fileKey, false)
      if (this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION) {
        const originQuestion = questionInfo._question
        if (originQuestion.type === OPEN_QUESTION_TYPE.VIDEO) {
          let resp = await this.handleOpenQuestionUploadedVideo({
            file_key: fileKey
          })
          const persistentId = resp.persistent_id
          const maxDuration = parseFloat(originQuestion.duration.slice(0, -1))
          if (maxDuration) {
            let duration = resp.duration
            let isFinished = resp.is_finished
            while (!isFinished) {
              resp = await this.getOpenQuestionUploadedVideoDuration({ persistent_id: persistentId })
              duration = resp.duration
              isFinished = resp.is_finished
              await new Promise((resolve, reject) => {
                setTimeout(resolve, 1000)
              })
            }
            if (duration > maxDuration) {
              this.$message.warning(this.$t(`${this.i18nPrefix}.message.questionVideoIsTooLong`, { duration: originQuestion.duration }))
            }
          }
        }
      }
      callback()
      questionInfo.answer = this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION ? fileKey : `${fileKey} ${fileName}`
      questionInfo.isUploading = false
    },
    validate () {
      let errorQuestion = _.find(this.questionList, 'errorMessage')
      if (!errorQuestion) {
        errorQuestion = _.find(this.questionList, 'isUploading')
        if (errorQuestion) {
          this.$message.warning(this.$t(`${this.i18nPrefix}.message.uploadTip.waitUploaded`))
        }
      }
      if (errorQuestion) {
        const questionEl = this.$refs[`q_${errorQuestion.id}`][0]
        questionEl.$el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        return false
      }
      return true
    },
    submit () {
      // await allUpload
      const isValid = this.validate()
      if (!isValid) {
        return isValid
      }
      _.forEach(this.questionList, questionInfo => {
        const originQuestion = questionInfo._question
        if (this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION) {
          originQuestion.answer_value = questionInfo.answer
          if ([OPEN_QUESTION_TYPE.VIDEO, OPEN_QUESTION_TYPE.ATTACHMENT].includes(originQuestion.type)) {
            originQuestion.answer_file_key = questionInfo.answer
            originQuestion.answer_value = questionInfo.uploaderFileDescription
          } else if (originQuestion.type === OPEN_QUESTION_TYPE.CHECKBOX || originQuestion.type === OPEN_QUESTION_TYPE.MULTIPLE_SELECT) {
            originQuestion.answer_value = _.join(questionInfo.answer)
          }
        } else {
          if (originQuestion.type === ONBOARDING_QUESTION_TYPE.LINK.value) {
            originQuestion.answer = questionInfo.answer ? '1' : '0'
          } else {
            originQuestion.answer = questionInfo.answer
          }
        }
      })
      return _.map(this.questionList, item => item._question)
    }
  },
  watch: {
    questionListWatcher: {
      handler () {
        const formatter = this.questionnaireType === QUESTIONNAIRE_TYPE.OPEN_QUESTION ? this.formatOpenQuestion : this.formatOnboardingQuestion
        this.questionList = formatter(this.questionnaire)
      },
      immediate: true,
      deep: true
    }

  }
}
</script>

<style lang="scss" scoped>
.questionnaire-container {
  .open-question-container {
    margin-bottom: 40px;
    padding: 20px;
    border: 1px dashed #8a8a8a;
    position: relative;

    &:last-child {
      margin-bottom: 0;
    }

    .container-title {
      background: #f7f7f7;
      font-size: 16px;
      font-weight: bold;
      transform: translateY(-50%);
      position: absolute;
      top: 0px;
      left: 10px;
    }

    .container-tip {
      margin-bottom: 10px;
    }
  }
}
</style>
